import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, PageHeader } from "@/components/kit";
import { TID } from "@/constants/testIds";
import {
  CaretLeft,
  UserCircle,
  IdentificationBadge,
  IdentificationCard,
  UploadSimple,
  Camera,
  X,
} from "@phosphor-icons/react";
import NomineesEditor from "@/components/NomineesEditor";

export default function NewCustomerPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [docFiles, setDocFiles] = useState({ photo: null, pan: null, aadhaar: null });
  const [nominees, setNominees] = useState([]);
  const fileInputs = useRef({});
  const cameraInputs = useRef({});

  useEffect(() => {
    (async () => {
      const b = await api.get("/branches");
      setBranches(b.data);
      if (user.role !== "agent") {
        try {
          const u = await api.get("/users");
          setAgents(u.data.filter((x) => x.role === "agent"));
        } catch { /* not permitted */ }
      }
    })();
  }, [user]);

  const pickFile = (kind, file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      setErr(`${kind} file is too large (max 5 MB).`);
      return;
    }
    setErr("");
    setDocFiles((prev) => ({ ...prev, [kind]: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    const fd = new FormData(e.target);
    const body = Object.fromEntries(fd.entries());
    ["doc_photo", "doc_pan", "doc_aadhaar"].forEach((k) => delete body[k]);
    ["email", "address", "id_type", "id_number", "agent_id", "customer_code"].forEach((k) => {
      if (body[k] === "" || body[k] == null) delete body[k];
    });
    if (!body.branch_id) body.branch_id = user.branch_id || branches[0]?.id;
    const cleanNominees = (nominees || [])
      .filter((n) => n && n.name && n.name.trim())
      .map((n) => ({ ...n, share_pct: n.share_pct === "" ? null : n.share_pct }));
    if (cleanNominees.length) body.nominees = cleanNominees;
    try {
      const created = await api.post("/customers", body);
      const custId = created.data.id;
      const uploads = [];
      for (const kind of ["photo", "pan", "aadhaar"]) {
        const file = docFiles[kind];
        if (!file) continue;
        const dfd = new FormData();
        dfd.append("kind", kind);
        dfd.append("file", file);
        uploads.push(
          api.post(`/customers/${custId}/documents`, dfd, {
            headers: { "Content-Type": "multipart/form-data" },
          }).catch((upErr) => ({ __error: upErr, kind }))
        );
      }
      const results = await Promise.all(uploads);
      const failed = results.filter((r) => r && r.__error);
      if (failed.length) {
        // Still navigate but mention it
        alert(`Customer saved, but ${failed.length} document upload(s) failed. You can retry from the customer page.`);
      }
      nav(`/customers/${custId}`);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message || "Failed");
      setSaving(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="new-customer-page">
      <Link to="/customers" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4 hover:text-slate-800">
        <CaretLeft size={14} /> Back to customers
      </Link>

      <PageHeader
        title="Add customer"
        subtitle="Enroll a new member into the cooperative."
      />

      <form onSubmit={submit} className="space-y-6">
        <Card className="p-6 space-y-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Basic details</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-3">
            <Input name="name" label="Full name" required testId="customer-name" />
            <Input name="customer_code" label="Account number (optional)" placeholder="e.g. CUS-000045 or 1024" testId="customer-code" />
          </div>
          <div className="text-[11px] text-slate-500 -mt-2">
            Leave the account number blank to auto-generate (CUS-000001, 000002…). Any value you enter must be unique.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input name="phone" label="Phone" required testId="customer-phone" />
            <Input name="email" type="email" label="Email (optional)" testId="customer-email" />
          </div>
          <Input name="address" label="Address" testId="customer-address" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input name="id_type" label="ID type (Aadhaar/PAN)" testId="customer-idtype" />
            <Input name="id_number" label="ID number" testId="customer-idnum" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {user.role === "super_admin" && (
              <Select name="branch_id" label="Branch" required testId="customer-branch">
                <option value="">Select branch</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
              </Select>
            )}
            {user.role !== "agent" && agents.length > 0 && (
              <Select name="agent_id" label="Assign agent (optional)" testId="customer-agent">
                <option value="">— none —</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            )}
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
            Nominees (optional)
          </div>
          <NomineesEditor value={nominees} onChange={setNominees} compact />
        </Card>

        <Card className="p-6 space-y-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
            KYC documents (optional — can also be added later)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <DocPicker
              label="Photo" kind="photo" icon={UserCircle} accept="image/*"
              file={docFiles.photo}
              onPick={(f) => pickFile("photo", f)}
              fileRef={(el) => (fileInputs.current.photo = el)}
              cameraRef={(el) => (cameraInputs.current.photo = el)}
              allowCamera
            />
            <DocPicker
              label="PAN Card" kind="pan" icon={IdentificationBadge} accept="image/*,application/pdf"
              file={docFiles.pan}
              onPick={(f) => pickFile("pan", f)}
              fileRef={(el) => (fileInputs.current.pan = el)}
            />
            <DocPicker
              label="Aadhaar" kind="aadhaar" icon={IdentificationCard} accept="image/*,application/pdf"
              file={docFiles.aadhaar}
              onPick={(f) => pickFile("aadhaar", f)}
              fileRef={(el) => (fileInputs.current.aadhaar = el)}
            />
          </div>
        </Card>

        {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="new-customer-error">{String(err)}</div>}

        <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-slate-200 -mx-4 md:-mx-8 px-4 md:px-8 py-3 flex justify-end gap-2 z-10">
          <Button variant="secondary" type="button" onClick={() => nav("/customers")} data-testid={TID.cancelBtn}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid={TID.saveBtn}>{saving ? "Saving…" : "Save customer"}</Button>
        </div>
      </form>
    </div>
  );
}

function DocPicker({ label, kind, icon: Icon, accept, file, onPick, fileRef, cameraRef, allowCamera = false }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 flex flex-col gap-2" data-testid={`new-doc-slot-${kind}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500">
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-slate-800">{label}</div>
          {file ? (
            <div className="text-[11px] mono text-emerald-700 truncate">{file.name}</div>
          ) : (
            <div className="text-[11px] text-slate-400">Not attached</div>
          )}
        </div>
        {file && (
          <button
            type="button"
            onClick={() => onPick(null)}
            className="text-slate-400 hover:text-rose-600"
            data-testid={`new-doc-clear-${kind}`}
            title="Remove"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      <div className="flex gap-1.5">
        {allowCamera && (
          <button
            type="button"
            onClick={() => cameraRef && document.getElementById(`new-doc-camera-${kind}`)?.click()}
            className="flex-1 flex items-center justify-center gap-1 text-xs px-2 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white font-medium"
            data-testid={`new-doc-camera-btn-${kind}`}
          >
            <Camera size={13} weight="bold" /> Camera
          </button>
        )}
        <button
          type="button"
          onClick={() => document.getElementById(`new-doc-file-${kind}`)?.click()}
          className="flex-1 flex items-center justify-center gap-1 text-xs px-2 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium"
          data-testid={`new-doc-upload-${kind}`}
        >
          <UploadSimple size={13} /> {file ? "Change" : "Choose"}
        </button>
      </div>
      <input
        id={`new-doc-file-${kind}`}
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={`new-doc-input-${kind}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          onPick(f || null);
        }}
      />
      {allowCamera && (
        <input
          id={`new-doc-camera-${kind}`}
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          data-testid={`new-doc-camera-input-${kind}`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            onPick(f || null);
          }}
        />
      )}
    </div>
  );
}
